/* This is the Clovis rights screen to explain our users what are the rights of the app 
  it is public because we link to it thru our public help center docs */
import { Language } from "@clovis/server/src/app/config/i18next/languages/allowed-languages";
import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { getCountryFromBrowser } from "~/config/i18next/helpers";
import { Box } from "~/design-system/Box";
import { SpinLoader } from "~/design-system/Spinner/SpinLoader";
import { createComponentHook } from "~/types";
import type { LanguageType } from "./components/LanguageDropdown/LanguageDropdown";
import { RightsDetailPage } from "./components/RightsDetailPage/RightsDetailPage";
import { RightsHeader } from "./components/RightsHeader/RightsHeader";
import { RightsMainPage } from "./components/RightsMainPage/RightsMainPage";

type IntercomSavedType = Record<"user", Record<"locale", string>> | null;

const useRights = createComponentHook(() => {
  const [language, setLanguage] = React.useState<LanguageType>("en");
  const { languageCode } = getCountryFromBrowser();
  const languagesList = Object.values(Language);
  const location = useLocation();

  React.useEffect(() => {
    const lang = (
      JSON.parse(
        localStorage.getItem("intercom.intercom-state") ?? "{}"
      ) as IntercomSavedType
    )?.user?.locale;

    /* Because this screen is not inside the app, 
      we need to get the language of the browser, or previsouly connected user*/
    if (lang) {
      setLanguage(
        !languagesList.includes(lang as Language)
          ? "en"
          : (lang as LanguageType)
      );
    } else {
      setLanguage(
        !languagesList.includes(languageCode as Language)
          ? "en"
          : (languageCode as LanguageType)
      );
    }
  }, []);

  // Handle hash navigation for sections
  React.useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  return {
    actions: { setLanguage },
    state: { language },
  };
});

function RightsScreen() {
  const { actions, state } = useRights();
  const { t } = useTranslation();
  return (
    <Box
      css={{
        height: "100vh",
        overflowY: "auto",
        paddingTop: "$space$2", // Minimal top padding instead of ScrollableContent's large padding
      }}
    >
      <React.Suspense fallback={<SpinLoader />}>
        <Box css={{ height: "100%" }} paddingX="medium">
          <RightsHeader
            language={state.language}
            setLanguage={actions.setLanguage}
          />
          <Routes>
            <Route
              path="/"
              element={<RightsMainPage language={state.language} />}
            />
            <Route
              path="project"
              element={
                <RightsDetailPage type="project" language={state.language} />
              }
            />
            <Route
              path="organization"
              element={
                <RightsDetailPage type="org" language={state.language} />
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </React.Suspense>
    </Box>
  );
}

export { RightsScreen };
